import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Phone, Mail, MapPin, Users, Languages } from 'lucide-react';

interface HSSProvider {
  id: string;
  name: string;
  agency_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  counties_served?: string[];
  populations_served?: string[];
  languages_spoken?: string[];
  accepting_clients: boolean;
  bio?: string;
  preferred_contact_method?: string;
}

interface HSSProviderCardProps {
  provider: HSSProvider;
  onContact: (provider: HSSProvider) => void;
}

const HSSProviderCard: React.FC<HSSProviderCardProps> = ({ provider, onContact }) => {
  return (
    <Card className="h-full hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{provider.agency_name || provider.name}</CardTitle>
            <CardDescription className="flex items-center gap-2 mt-1">
              <MapPin className="h-4 w-4" />
              {provider.city && provider.state ? `${provider.city}, ${provider.state}` : 'Location not specified'}
            </CardDescription>
          </div>
          <Badge variant={provider.accepting_clients ? "default" : "secondary"}>
            {provider.accepting_clients ? 'Accepting Referrals' : 'Not Accepting'}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {provider.bio && (
          <p className="text-sm text-muted-foreground line-clamp-3">{provider.bio}</p>
        )}
        
        {provider.counties_served && provider.counties_served.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-2">Counties Served</h4>
            <div className="flex flex-wrap gap-1">
              {provider.counties_served.slice(0, 3).map((county) => (
                <Badge key={county} variant="outline" className="text-xs">
                  {county}
                </Badge>
              ))}
              {provider.counties_served.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{provider.counties_served.length - 3} more
                </Badge>
              )}
            </div>
          </div>
        )}
        
        {provider.populations_served && provider.populations_served.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
              <Users className="h-3 w-3" />
              Populations Served
            </h4>
            <div className="flex flex-wrap gap-1">
              {provider.populations_served.slice(0, 2).map((population) => (
                <Badge key={population} variant="outline" className="text-xs">
                  {population}
                </Badge>
              ))}
              {provider.populations_served.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{provider.populations_served.length - 2} more
                </Badge>
              )}
            </div>
          </div>
        )}
        
        {provider.languages_spoken && provider.languages_spoken.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
              <Languages className="h-3 w-3" />
              Languages
            </h4>
            <div className="flex flex-wrap gap-1">
              {provider.languages_spoken.slice(0, 2).map((language) => (
                <Badge key={language} variant="outline" className="text-xs">
                  {language}
                </Badge>
              ))}
              {provider.languages_spoken.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{provider.languages_spoken.length - 2} more
                </Badge>
              )}
            </div>
          </div>
        )}
        
        <div className="pt-4 border-t">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Prefers: {provider.preferred_contact_method === 'phone' ? 'Phone' : 'Email'}
            </div>
            <Button 
              size="sm" 
              onClick={() => onContact(provider)}
              disabled={!provider.accepting_clients}
            >
              {provider.accepting_clients ? 'Contact Provider' : 'Not Available'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default HSSProviderCard;